import React, { useState, useEffect } from 'react';
import { post } from '../../services/ApiService';

function StepThree({ formData, onNext }) {
    const [passportFile, setPassportFile] = useState(null);
    const [residencyFile, setResidencyFile] = useState(null);
    const [idCardFile, setIdCardFile] = useState(null);
    const [workVisaFile, setWorkVisaFile] = useState(null);
    const [error, setError] = useState('');
    const [uploadStep, setUploadStep] = useState(1);
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState({
        passport: formData.passportFile || null,
        residency: formData.residencyFile || null,
        idCard: formData.idCardFile || null,
        workVisa: formData.workVisaFile || null
    });
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    // Initialize file states from formData when component mounts
    useEffect(() => {
        // Set upload step based on existing files
        if (formData.hasUKPassport === 'yes') {
            if (formData.passportFile) {
                setUploadStep(2);
            }
        } else {
            if (formData.residencyFile) {
                setUploadStep(2);
            }
            if (formData.idCardFile) {
                setUploadStep(3);
            }
            if (formData.workVisaFile) {
                setUploadStep(4);
            }
        }
    }, [formData]);

    const handleContinue = () => {
        if (formData.hasUKPassport === 'yes') {
            if (!uploadedFiles.passport) {
                setError('Please upload your UK passport.');
                return;
            }
        } else {
            if (!uploadedFiles.residency || !uploadedFiles.workVisa) {
                setError('Please upload residency and work visa documents.');
                return;
            }
        }

        setError('');
        onNext({ 
            passportFile: uploadedFiles.passport, 
            residencyFile: uploadedFiles.residency, 
            idCardFile: uploadedFiles.idCard, 
            workVisaFile: uploadedFiles.workVisa 
        });
    };

    const onUpload = async (file, fileType) => {
        const formDataObj = new FormData();
        formDataObj.append("file", file);
        
        try {
            // Reset progress for this file type
            setUploadProgress(prev => ({
                ...prev,
                [fileType]: 0
            }));

            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => ({
                    ...prev,
                    [fileType]: prev[fileType] < 90 ? prev[fileType] + 10 : 90
                }));
            }, 200);

            const res = await post("home/file-upload", formDataObj, true);
            console.log(res);
            clearInterval(progressInterval);
            setUploadProgress(prev => ({
                ...prev,
                [fileType]: 100
            }));
            
            // Small delay to show 100% completion
            await new Promise(resolve => setTimeout(resolve, 300));
            
            if (res.success) {
                const fileName = res.file;
                setUploadedFiles(prev => ({
                    ...prev,
                    [fileType]: fileName
                }));
                return fileName;
            } else {
                throw new Error(res.message || "Upload failed");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            setUploadProgress(prev => ({
                ...prev,
                [fileType]: 0
            }));
            throw error;
        }
    };

    const onRemove = async (fileType) => {
        const filename = uploadedFiles[fileType];
        console.log(filename)
        if (!filename) return;

        try {
            const res = await post("home/file-remove", { filename: filename });
            if (res.success) {
                // Remove file from state
                setUploadedFiles(prev => {
                    const newFiles = { ...prev };
                    delete newFiles[fileType];
                    return newFiles;
                });

                // Reset file input
                switch (fileType) {
                    case 'passport':
                        setPassportFile(null);
                        setUploadStep(1);
                        break;
                    case 'residency':
                        setResidencyFile(null);
                        setUploadStep(1);
                        break;
                    case 'idCard':
                        setIdCardFile(null);
                        setUploadStep(2);
                        break;
                    case 'workVisa':
                        setWorkVisaFile(null);
                        break;
                    default:
                        break;
                }

                // Reset progress
                setUploadProgress(prev => ({
                    ...prev,
                    [fileType]: 0
                }));
            }
            return res;
        } catch (error) {
            console.error("Error in file removal:", error);
            throw error;
        }
    };

    const handleFileChange = async (e, fileType, nextStep) => {
        const file = e.target.files[0];
        if (!file) return;

        // Set file immediately for preview
        switch (fileType) {
            case 'passport':
                setPassportFile(file);
                break;
            case 'residency':
                setResidencyFile(file);
                break;
            case 'idCard':
                setIdCardFile(file);
                break;
            case 'workVisa':
                setWorkVisaFile(file);
                break;
            default:
                break;
        }

        // Upload file
        try {
            await onUpload(file, fileType);
            if (nextStep) {
                setUploadStep(nextStep);
            }
        } catch (error) {
            setError(`Failed to upload ${fileType}. Please try again.`);
            // Reset file on error
            switch (fileType) {
                case 'passport':
                    setPassportFile(null);
                    break;
                case 'residency':
                    setResidencyFile(null);
                    break;
                case 'idCard':
                    setIdCardFile(null);
                    break;
                case 'workVisa':
                    setWorkVisaFile(null);
                    break;
                default:
                    break;
            }
        }
    };

    const renderFilePreview = (file, fileType, onDelete) => {
        const fileName = uploadedFiles[fileType];
        const progress = uploadProgress[fileType] || 0;
        const isUploaded = !!fileName;

        // If we have a file object (new upload) or fileName (existing file)
        const displayFile = file || (fileName ? { name: fileName } : null);
        
        if (!displayFile) return null;

        const isNewFile = file && !fileName; // File selected but not uploaded yet
        const isExistingFile = !file && fileName; // File already uploaded previously
        
        // For existing files, we can show a preview if it's an image
        const isImage = file ? file.type.startsWith('image/') : 
                              fileName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
        
        const previewURL = file ? URL.createObjectURL(file) : 
                          `${backendUrl}/uploads/${fileName}`;

        return (
            <div className="file_prev">
                <div className="d-flex align-items-center justify-content-between gap-3 mt-2">
                    <div className="d-flex align-items-center gap-2">
                        {isImage && (
                            <img
                                src={previewURL}
                                alt="preview"
                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                onError={(e) => {
                                    // Hide image if it fails to load (e.g., not an image file)
                                    e.target.style.display = 'none';
                                }}
                            />
                        )}
                        <div>
                            <span>{displayFile.name}</span>
                            {isUploaded && (
                                <div className="text-success small">
                                    {isExistingFile ? 'Previously uploaded: ' : 'Uploaded: '}{fileName}
                                </div>
                            )}
                            {isNewFile && progress === 0 && (
                                <div className="text-warning small">Ready to upload</div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {progress > 0 && progress < 100 && (
                            <div className="progress" style={{ width: '100px', height: '8px' }}>
                                <div 
                                    className="progress-bar" 
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                        )}
                        <button 
                            className="border-0 bg-transparent text-danger" 
                            onClick={onDelete}
                            disabled={progress > 0 && progress < 100}
                        >
                            <img src="assets/bin_icon.png" alt="delete" style={{ width: '15px' }} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const isNextDisabled = formData.hasUKPassport === 'yes'
        ? !uploadedFiles.passport
        : !uploadedFiles.residency || !uploadedFiles.workVisa;

    return (
        <div className="step-form">
            <h6 className="mb-4">ID verification and right to work</h6>
            {error && <div className="alert alert-danger">{error}</div>}

            {formData.hasUKPassport === 'yes' ? (
                <>
                    {uploadStep >= 1 && (
                        <div className="mb-3">
                            <label className="form-label">Please upload a copy of your UK passport. <span className="text-danger">*</span></label>
                            <div className="upl_file">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => handleFileChange(e, 'passport', 2)}
                                />
                                <p className="drag_text text-center">
                                    Drag your file(s) here or <b>Browse</b>
                                    <span className="d-block">Max 1.5mb file size</span>
                                </p>
                            </div>
                            {/* Show preview for existing file or newly selected file */}
                            {(uploadedFiles.passport || passportFile) && 
                                renderFilePreview(passportFile, 'passport', () => onRemove('passport'))
                            }
                        </div>
                    )}
                </>
            ) : (
                <>
                    {uploadStep >= 1 && (
                        <div className="mb-3">
                            <label className="form-label">Please upload Evidence of permanent residency in the UK <span className="text-danger">*</span></label>
                            <div className="upl_file">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => handleFileChange(e, 'residency', 2)}
                                />
                                <p className="drag_text text-center">
                                    Drag your file(s) here or <b>Browse</b>
                                    <span className="d-block">Max 1.5mb file size</span>
                                </p>
                            </div>
                            {(uploadedFiles.residency || residencyFile) && 
                                renderFilePreview(residencyFile, 'residency', () => onRemove('residency'))
                            }
                        </div>
                    )}

                    {uploadStep >= 2 && (
                        <div className="mb-3">
                            <label className="form-label">National identity card (if applicable)</label>
                            <div className="upl_file">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => handleFileChange(e, 'idCard', 3)}
                                />
                                <p className="drag_text text-center">
                                    Drag your file(s) here or <b>Browse</b>
                                    <span className="d-block">Max 1.5mb file size</span>
                                </p>
                            </div>
                            {(uploadedFiles.idCard || idCardFile) && 
                                renderFilePreview(idCardFile, 'idCard', () => onRemove('idCard'))
                            }
                        </div>
                    )}

                    {uploadStep >= 3 && (
                        <div className="mb-3">
                            <label className="form-label">Evidence of right to work in the UK <span className="text-danger">*</span></label>
                            <div className="upl_file">
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={(e) => handleFileChange(e, 'workVisa')}
                                />
                                <p className="drag_text text-center">
                                    Drag your file(s) here or <b>Browse</b>
                                    <span className="d-block">Max 1.5mb file size</span>
                                </p>
                            </div>
                            {(uploadedFiles.workVisa || workVisaFile) && 
                                renderFilePreview(workVisaFile, 'workVisa', () => onRemove('workVisa'))
                            }
                        </div>
                    )}
                </>
            )}

            <div className="text-end mt-5">
                <button
                    className="btn btn-dark px-4"
                    onClick={handleContinue}
                    disabled={isNextDisabled}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

export default StepThree;